import { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, ShieldCheck, Mail, Sparkles, Lock, Instagram, Music, Youtube } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { legalApi, reportsApi, type CurrentLegalVersions } from '../lib/reportsApi';
import Turnstile from '../components/Turnstile';
import { Button, Input, Checkbox, Badge } from '../components/ui/light';

type Role = 'creator' | 'vendor';
type Step = 'capture' | 'consent' | 'verify' | 'generating';

export default function Signup() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialRole = (searchParams.get('role') === 'vendor' ? 'vendor' : 'creator') as Role;

  const [step, setStep] = useState<Step>('capture');
  const [role, setRole] = useState<Role>(initialRole);

  const [profileUrl, setProfileUrl] = useState(searchParams.get('profile_url') ?? '');
  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');

  const [acceptedTos, setAcceptedTos] = useState(false);
  const [marketingOptin, setMarketingOptin] = useState(false);
  const [legalVersions, setLegalVersions] = useState<CurrentLegalVersions | null>(null);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const codeRefs = useRef<(HTMLInputElement | null)[]>([]);

  // ── Load current legal versions once ────────────────────────────
  useEffect(() => {
    legalApi.current().then((r) => setLegalVersions(r.data)).catch(() => { /* silent */ });
  }, []);

  const handleTurnstileVerify = useCallback((token: string) => setTurnstileToken(token), []);
  const handleTurnstileError = useCallback(() => {
    setError('Verification failed. Please refresh and try again.');
    setTurnstileToken('');
  }, []);
  const handleTurnstileExpire = useCallback(() => setTurnstileToken(''), []);

  // ── Step 1: capture form → step 2 (consent) ─────────────────────
  const handleCaptureSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) { setError('Email is required'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (role === 'vendor' && !companyName.trim()) { setError('Company name is required'); return; }
    setStep('consent');
  };

  // ── Step 2: consent → submit signup → step 3 (verify) ──────────
  const handleConsentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!acceptedTos) { setError('You must accept the Terms of Service and Privacy Policy to continue.'); return; }
    if (!legalVersions) { setError('Legal documents could not be loaded. Refresh and try again.'); return; }

    setLoading(true);
    try {
      // Record pre-signup consent (the row is later linked to the
      // user_id by email lookup once the account exists).
      await legalApi.recordConsent({
        email: email.trim(),
        terms_version: legalVersions.terms_version,
        privacy_version: legalVersions.privacy_version,
        marketing_optin: marketingOptin,
      });

      await api.signup({
        role,
        email: email.trim(),
        password,
        name: name.trim(),
        ...(role === 'vendor' ? { company_name: companyName.trim() } : {}),
        turnstileToken: turnstileToken || undefined,
      });
      setStep('verify');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Signup failed');
      setTurnstileToken('');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 3: verify ─────────────────────────────────────────────
  const handleVerify = async () => {
    const fullCode = code.join('');
    if (fullCode.length !== 6) { setError('Enter the full 6-digit code'); return; }
    setError('');
    setLoading(true);
    try {
      await api.signupVerify({ email: email.trim(), code: fullCode });
      // Re-record consent now that the user_id exists, so the
      // consents row is linked to the account.
      if (legalVersions) {
        try {
          await legalApi.recordConsent({
            email: email.trim(),
            terms_version: legalVersions.terms_version,
            privacy_version: legalVersions.privacy_version,
            marketing_optin: marketingOptin,
          });
        } catch { /* non-fatal */ }
      }
      await refresh();

      // Vendor accounts skip report auto-generation.
      if (role === 'vendor') {
        navigate('/vendor/dashboard');
        return;
      }

      // If the user came in with a profile URL, kick off generation.
      if (profileUrl.trim()) {
        setStep('generating');
        try {
          const res = await reportsApi.generate({ profile_url: profileUrl.trim() });
          navigate(`/reports/${res.data.report_id}`);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Report generation failed');
          navigate('/reports');
        }
      } else {
        navigate('/reports');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setLoading(true);
    try {
      await api.signup({
        role,
        email: email.trim(),
        password,
        name: name.trim(),
        ...(role === 'vendor' ? { company_name: companyName.trim() } : {}),
        turnstileToken: turnstileToken || undefined,
      });
      setCode(['', '', '', '', '', '']);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      digits.forEach((d, i) => { if (index + i < 6) newCode[index + i] = d; });
      setCode(newCode);
      const nextIdx = Math.min(index + digits.length, 5);
      codeRefs.current[nextIdx]?.focus();
      return;
    }
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) codeRefs.current[index + 1]?.focus();
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
    if (e.key === 'Enter') handleVerify();
  };

  useEffect(() => {
    if (step === 'verify') codeRefs.current[0]?.focus();
  }, [step]);

  return (
    <div className="theme-light l-page min-h-screen flex flex-col">
      {/* ── Top nav ─────────────────────────────────────────── */}
      <header className="py-5">
        <div className="l-shell flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div
              className="w-7 h-7 flex items-center justify-center"
              style={{ background: 'var(--ch2-ink)', borderRadius: '8px', color: '#FFD400' }}
            >
              <span className="font-display font-bold text-xs">C</span>
            </div>
            <span className="font-display font-semibold tracking-tight" style={{ fontSize: 17, color: 'var(--ch2-ink)' }}>
              CreatorHub<span style={{ color: 'var(--ch2-accent)' }}>360</span>
            </span>
          </Link>
          <Link to="/login" className="l-btn l-btn-ghost l-btn-sm">
            Already have an account?
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 py-10">
        <div className="w-full max-w-[460px]">
          <StepIndicator step={step} role={role} />

          <div className="l-card-elevated p-7 lg:p-8">
            {/* Title */}
            <div className="mb-6">
              <h1 className="l-display-3 mb-1.5" style={{ color: 'var(--ch2-ink)' }}>
                {step === 'capture' && (role === 'vendor' ? 'Create your vendor account' : 'Create your account')}
                {step === 'consent' && 'Just one more thing'}
                {step === 'verify'  && 'Verify your email'}
                {step === 'generating' && 'Generating your report'}
              </h1>
              <p className="l-muted text-sm">
                {step === 'capture' && (role === 'vendor'
                  ? 'Sell into top creator storefronts in the CreatorHub network.'
                  : 'Get your free creator monetization report.')}
                {step === 'consent' && 'Accept our terms and you\'re ready to go.'}
                {step === 'verify'  && `Enter the 6-digit code we sent to ${email}.`}
                {step === 'generating' && 'Pulling your audience signals from the platform…'}
              </p>
            </div>

            {/* Error toast */}
            {error && (
              <div
                className="p-3 mb-5 text-sm"
                style={{
                  background: 'var(--ch2-danger-tint)',
                  color: 'var(--ch2-danger)',
                  borderRadius: 'var(--ch2-radius-sm)',
                  border: '1px solid rgba(225,29,72,0.18)',
                }}
              >
                {error}
              </div>
            )}

            {step === 'capture' && (
              <form onSubmit={handleCaptureSubmit} className="space-y-4">
                {/* Role pill */}
                <div
                  className="grid grid-cols-2 gap-1 p-1 mb-1"
                  style={{ background: 'var(--ch2-surface-2)', borderRadius: 'var(--ch2-radius-sm)' }}
                >
                  {(['creator', 'vendor'] as const).map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setRole(r)}
                      className="py-2 text-sm font-medium transition-all"
                      style={{
                        background: role === r ? 'var(--ch2-surface)' : 'transparent',
                        color:      role === r ? 'var(--ch2-ink)'     : 'var(--ch2-ink-2)',
                        boxShadow:  role === r ? 'var(--ch2-shadow-sm)' : undefined,
                        borderRadius: 'var(--ch2-radius-sm)',
                      }}
                    >
                      {r === 'creator' ? 'I\'m a creator' : 'I\'m a vendor'}
                    </button>
                  ))}
                </div>

                {role === 'creator' && (
                  <Input
                    label="Profile URL (optional)"
                    placeholder="https://instagram.com/your-handle"
                    value={profileUrl}
                    onChange={(e) => setProfileUrl(e.target.value)}
                    hint="We'll auto-generate your first report after verification."
                  />
                )}

                <Input
                  label={role === 'vendor' ? 'Contact name' : 'Full name'}
                  placeholder="Jane Smith"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoComplete="name"
                />

                {role === 'vendor' && (
                  <Input
                    label="Company name"
                    placeholder="Acme Inc."
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                    autoComplete="organization"
                  />
                )}

                <Input
                  label="Email"
                  type="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
                <Input
                  label="Password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />
                <Input
                  label="Confirm password"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                />

                <Button type="submit" variant="accent" size="lg" className="w-full">
                  Continue
                </Button>

                {role === 'creator' && (
                  <div className="flex items-center gap-2 pt-1 flex-wrap">
                    <Badge variant="neutral"><Instagram className="w-3 h-3 mr-1 inline" />Instagram</Badge>
                    <Badge variant="neutral"><Music className="w-3 h-3 mr-1 inline" />TikTok</Badge>
                    <Badge variant="neutral"><Youtube className="w-3 h-3 mr-1 inline" />YouTube</Badge>
                  </div>
                )}
              </form>
            )}

            {step === 'consent' && (
              <form onSubmit={handleConsentSubmit} className="space-y-5">
                <div
                  className="p-4"
                  style={{
                    background: 'var(--ch2-surface-2)',
                    border: '1px solid var(--ch2-border-2)',
                    borderRadius: 'var(--ch2-radius-sm)',
                  }}
                >
                  <Checkbox
                    id="tos"
                    checked={acceptedTos}
                    onChange={(e) => setAcceptedTos(e.target.checked)}
                    label={
                      <>
                        I agree to the{' '}
                        <Link to="/legal/terms" target="_blank" style={{ color: 'var(--ch2-accent-2)' }}>
                          Terms of Service
                        </Link>{' '}
                        and{' '}
                        <Link to="/legal/privacy" target="_blank" style={{ color: 'var(--ch2-accent-2)' }}>
                          Privacy Policy
                        </Link>
                        .
                      </>
                    }
                    hint={legalVersions ? `v${legalVersions.terms_version} / v${legalVersions.privacy_version}` : undefined}
                    required
                  />
                </div>
                <Checkbox
                  id="marketing"
                  checked={marketingOptin}
                  onChange={(e) => setMarketingOptin(e.target.checked)}
                  label="Email me product updates and creator tips. (Optional — no spam, unsubscribe anytime.)"
                />

                <Turnstile
                  onVerify={handleTurnstileVerify}
                  onError={handleTurnstileError}
                  onExpire={handleTurnstileExpire}
                />

                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="secondary"
                    iconLeft={<ArrowLeft className="w-4 h-4" />}
                    onClick={() => setStep('capture')}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    variant="accent"
                    className="flex-1"
                    disabled={!acceptedTos || !turnstileToken || loading}
                    loading={loading}
                  >
                    Create my account
                  </Button>
                </div>
              </form>
            )}

            {step === 'verify' && (
              <div className="space-y-5">
                <div className="flex gap-2 justify-center">
                  {code.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => { codeRefs.current[i] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={digit}
                      onChange={(e) => handleCodeChange(i, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(i, e)}
                      className="l-input text-center font-bold"
                      style={{ width: 48, height: 56, padding: 0, fontSize: 20 }}
                    />
                  ))}
                </div>

                <Button
                  type="button"
                  variant="accent"
                  size="lg"
                  className="w-full"
                  loading={loading}
                  disabled={loading || code.join('').length !== 6}
                  onClick={handleVerify}
                >
                  Verify & continue
                </Button>

                <p className="text-center l-subtle text-xs">
                  Didn't receive it?{' '}
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={loading}
                    className="font-medium hover:underline"
                    style={{ color: 'var(--ch2-accent-2)' }}
                  >
                    Resend code
                  </button>
                </p>
              </div>
            )}

            {step === 'generating' && (
              <div className="flex flex-col items-center text-center py-10">
                <div
                  className="w-14 h-14 flex items-center justify-center mb-5"
                  style={{
                    background: 'var(--ch2-accent-tint)',
                    borderRadius: '999px',
                  }}
                >
                  <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--ch2-accent-2)' }} />
                </div>
                <p className="font-medium" style={{ color: 'var(--ch2-ink)' }}>
                  Pulling audience signals…
                </p>
                <p className="l-muted text-sm mt-1">This usually takes 20–30 seconds.</p>
              </div>
            )}

            {/* Trust strip on first two steps */}
            {(step === 'capture' || step === 'consent') && (
              <div className="mt-6 pt-5 flex items-center justify-center gap-5 text-xs flex-wrap" style={{ borderTop: '1px solid var(--ch2-border-2)', color: 'var(--ch2-ink-3)' }}>
                <span className="inline-flex items-center gap-1.5"><Lock className="w-3 h-3" /> Encrypted</span>
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> No spam</span>
                <span className="inline-flex items-center gap-1.5"><Mail className="w-3 h-3" /> Email verified</span>
                <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> Free</span>
              </div>
            )}
          </div>

          <p className="text-center l-subtle text-xs mt-5">
            Already have an account?{' '}
            <Link to="/login" className="font-medium hover:underline" style={{ color: 'var(--ch2-ink-2)' }}>
              Sign in
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}

function StepIndicator({ step, role }: { step: Step; role: Role }) {
  const map: Step[] = ['capture', 'consent', 'verify'];
  const idx = map.indexOf(step === 'generating' ? 'verify' : step);
  return (
    <div className="flex items-center justify-center gap-1 mb-6">
      {map.map((s, i) => (
        <div
          key={s}
          className="h-1"
          style={{
            width: 36,
            background: i <= idx ? 'var(--ch2-accent)' : 'var(--ch2-border)',
            borderRadius: 999,
          }}
          aria-label={`Step ${i + 1} (${role})`}
        />
      ))}
    </div>
  );
}
